import type Luminary from '@lumen5/luminary';

import type { Tool } from '../tools/Tool';
import {
    getDiffFormatGuide,
    getHowOurVideosWorkGuide,
} from '../video-state/serialization/formatting';

import { RootAgent, type RootAgentConfig } from './RootAgent';

import { getVideoAnnotationGuide } from 'js/src/agent';
import {
    LegacyAddAIVoiceoverSceneGroupTool,
    LegacyAddSceneTool,
    LegacyAddStickerTool,
    LegacyAddVoiceoverSceneTool,
    CapabilityCheckTool,
    LegacyDeleteSceneTool,
    GenerateAIMediaTool,
    GetSceneScreenshotWithDescriptionTool,
    IconSearchTool,
    LegacyImageAskTool,
    LintVideoTool,
    ListSceneDesignTool,
    MediaSearchTool,
    MusicSearchTool,
    SetAspectRatioTool,
    LegacySetAssetGroupTool,
    LegacySetBackgroundTool,
    LegacySetIconTool,
    LegacySetMediaTool,
    SetMusicTool,
    SetMusicVolumeTool,
    LegacySetSceneDurationTool,
    LegacySetTextPositionPresetTool,
    LegacySetTextTool,
    LegacySetWatermarkTool,
} from 'js/src/agent/tools';
import type { Skill } from 'js/src/agent/skills/types';
import type { AppDispatch } from 'js/src/libs/RootState';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

export function getDefaultSystemMessage(): string {
    return `\`You are a helpful video editing assistant. You can inspect and modify videos using the available tools.

${getHowOurVideosWorkGuide('scene_index')}

${getVideoAnnotationGuide()}

${getDiffFormatGuide()}

## Instructions
When users ask you to make changes, use the provided video structure to understand the current state, then make the requested modifications. Always explain what you're doing and why. Use markdown to structure your responses.

Be specific about what you find when inspecting videos and what changes you're making. Use the tools systematically to help users improve their videos.

When users reference colors by name (e.g., "red", "blue", "yellow"), automatically convert them to their corresponding hex codes (e.g., #ff0000) for precise color application.

Users can only use template fonts, you must use the closest available template font.

## Capability Checking
Use the ${CapabilityCheckTool.toolName} tool first when:
- A user asks for something that might not be possible in Lumen5
- A user requests an action you don't have a specific tool for
- A user asks about limitations or what you can/cannot do

## Linting
Before telling the user the video is finished, call \`${LintVideoTool.toolName}\` to run the quality checks. Fix every **error** it reports. For each **warning**, confirm it's intentional (e.g. placeholder flair you left on purpose, or a deliberately short scene) before finishing — surface it to the user if you're unsure.

## Output Format
Always format your responses using MARKDOWN syntax including bold, code formatting, bullet points, headers, and images.
`;
}

export class ChatRootAgent extends RootAgent {
    constructor(config: RootAgentConfig) {
        super({ ...config, requiresVideoState: true, maxIterations: config.maxIterations ?? 50 });
    }

    override getSystemMessage(): string {
        return getDefaultSystemMessage();
    }

    override getTaskName(): string {
        return 'chat';
    }

    public override getSkills(): Skill[] {
        return [];
    }

    override get feedbackEnabled(): boolean {
        return true;
    }

    override getContextMessage() {
        return Promise.resolve(null);
    }

    override getTools(luminary: Luminary, dispatch: AppDispatch, getState: GetRootState) {
        const optionalTools: Tool[] = this.model.startsWith('gpt-5')
            ? []
            : [new CapabilityCheckTool({ luminary, dispatch })];
        return optionalTools.concat([
            new IconSearchTool({ luminary, dispatch, getState }),
            new LegacySetIconTool({ luminary, dispatch }),
            new LegacySetBackgroundTool({ luminary, dispatch }),
            new LegacySetAssetGroupTool({ luminary, dispatch }),
            new MediaSearchTool({ luminary, dispatch, getState }),
            new GenerateAIMediaTool({ luminary, dispatch }),
            new LegacyImageAskTool({ luminary, dispatch }),
            new LegacySetMediaTool({ luminary, dispatch, getState }),
            new LegacySetSceneDurationTool({ luminary, dispatch, getState }),
            new LegacySetWatermarkTool({ luminary, dispatch }),
            new LegacySetTextTool({ luminary, dispatch, getState }),
            new LegacySetTextPositionPresetTool({ luminary, dispatch, getState }),
            new SetAspectRatioTool({ luminary, dispatch }),
            new LegacyAddSceneTool({ luminary, dispatch, getState }),
            new LegacyAddStickerTool({ luminary, dispatch, getState }),
            new LegacyAddAIVoiceoverSceneGroupTool({ luminary, dispatch, getState }),
            new LegacyAddVoiceoverSceneTool({ luminary, dispatch, getState }),
            new LegacyDeleteSceneTool({ luminary, dispatch, getState }),
            new ListSceneDesignTool({ luminary, dispatch, getState }),
            new GetSceneScreenshotWithDescriptionTool({ luminary, dispatch, getState }),
            new LintVideoTool({ luminary, dispatch, getState, sceneAddressing: 'index' }),
            new SetMusicTool({ luminary, dispatch }),
            new SetMusicVolumeTool({ luminary, dispatch }),
            new MusicSearchTool({ luminary, dispatch }),
        ]);
    }
}
